import { withStyles, withTheme } from '@glu/theming';
import { Fade } from '@glu/transitions-react';
import { useTrapFocus } from '@glu/utilities-react';
import {
  CheckmarkCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InfoCircleAshIcon as InfoCircleIcon
} from '@glu/icons-react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';
import { Button, TERTIARY } from '@glu/buttons-react';
import useKeyHandler from 'hooks/useKeyHandler';
import useModalFocus from 'hooks/useModalFocus';

import styles from './ModalNotification.styles';

const ModalNotification = ({
  children,
  className,
  classes,
  closeButton,
  dialogClassName,
  disableBackdropClick,
  emulation,
  footer,
  header,
  hideBackdrop,
  icon,
  iconTitle,
  mountOnEnter,
  onHide,
  show,
  suppressIcon,
  theme: {
    transitions: {
      duration: { short }
    }
  },
  transition: Transition,
  transition,
  unmountOnExit,
  variant
}) => {
  const [transitionIn, setTransitionIn] = useState(true);
  const modalRef = useRef();

  const transitionDone = (event) => {
    setTransitionIn(true);
    onHide(event);
  };

  const closeModal = (event) => {
    if (typeof transition === 'string') {
      onHide(event);
      return;
    }

    setTransitionIn(false);
    setTimeout(() => transitionDone(event), short);
  };

  const getIconComponent = () => {
    if (icon) {
      const Icon = icon;
      return Icon;
    }
    switch (variant) {
      case 'success':
        return CheckmarkCircleIcon;
      case 'error':
        return ExclamationCircleIcon;
      case 'warning':
        return ExclamationTriangleIcon;
      default:
        return InfoCircleIcon;
    }
  };

  const showTransition = show && transitionIn;

  const transitionProps =
    typeof Transition === 'string'
      ? {}
      : {
          in: show && transitionIn,
          mountOnEnter,
          unmountOnExit
        };

  const IconComponent = getIconComponent();

  const getAriaLabel = () => 'Dialog';

  // Focus modal on open, focus previous element on close
  useModalFocus({ ref: modalRef, active: show });

  // Escape key to close modal
  useKeyHandler({ keyEvent: 'keyup', keyCode: 27, callback: closeModal });

  const onKeyDown = useTrapFocus({ ref: modalRef, active: show });

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <section className={classes.modalComponent}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        data-qa="modal-notification"
        className={classNames(className, classes.root, { [classes.modalIn]: show })}
        role="dialog"
        tabIndex="-1"
        ref={modalRef}
        onKeyDown={onKeyDown}
        aria-label={getAriaLabel()}
      >
        {hideBackdrop ? null : (
          <Fade in={showTransition} mountOnEnter={mountOnEnter} unmountOnExit={unmountOnExit}>
            <div
              className={classes.backdrop}
              role="button"
              {...(disableBackdropClick
                ? {}
                : {
                    onClick: closeModal
                  })}
              tabIndex="0"
            />
          </Fade>
        )}

        <Transition {...transitionProps}>
          <article
            className={classNames(dialogClassName, classes.dialog, 'dialog', { emulation })}
            role="document"
          >
            <div className={classNames(classes.content, classes[variant])}>
              {header && (
                <header className={classes.header}>
                  {closeButton && (
                    <Button
                      type="button"
                      variant={TERTIARY}
                      className={classes.headerCloseBth}
                      onClick={closeModal}
                    >
                      <span aria-hidden="true">×</span>
                    </Button>
                  )}
                  {header}
                </header>
              )}
              <section className={classes.modalBody}>
                {!header && closeButton && (
                  <Button
                    type="button"
                    variant={TERTIARY}
                    className={classes.closeRounded}
                    onClick={closeModal}
                  >
                    <span className="sr-only">Close dialog</span>
                    <XCircleIcon title="Close dialog" aria-hidden />
                  </Button>
                )}
                {!suppressIcon && (
                  <IconComponent
                    aria-hidden
                    className={classes.checkmarkWrapper}
                    focusable="false"
                    title={iconTitle || variant}
                  />
                )}
                <div>{children}</div>
              </section>

              {footer && <footer className={classes.modalFooter}>{footer}</footer>}
            </div>
          </article>
        </Transition>
      </div>
    </section>
  );
};

ModalNotification.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.node,
    PropTypes.element
  ]),
  classes: PropTypes.objectOf(PropTypes.string),
  className: PropTypes.string,
  dialogClassName: PropTypes.string,
  closeButton: PropTypes.bool,
  footer: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
    PropTypes.string
  ]),
  hideBackdrop: PropTypes.bool,
  header: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
    PropTypes.string
  ]),
  icon: PropTypes.elementType,
  iconTitle: PropTypes.string,
  mountOnEnter: PropTypes.bool,
  onHide: PropTypes.func,
  show: PropTypes.bool,
  suppressIcon: PropTypes.bool,
  theme: PropTypes.shape({}),
  // Too many prop type errors. Takes string or react component
  // eslint-disable-next-line react/forbid-prop-types
  transition: PropTypes.any,
  unmountOnExit: PropTypes.bool,
  variant: PropTypes.oneOf(['info', 'success', 'error', 'warning']),
  disableBackdropClick: PropTypes.bool,
  emulation: PropTypes.bool
};

ModalNotification.defaultProps = {
  children: undefined,
  classes: {},
  className: 'modal',
  dialogClassName: '',
  closeButton: true,
  footer: null,
  header: null,
  hideBackdrop: null,
  icon: null,
  iconTitle: '',
  mountOnEnter: true,
  onHide() {},
  show: false,
  suppressIcon: true,
  theme: {
    transitions: {
      duration: {}
    }
  },
  transition: Fade,
  unmountOnExit: true,
  variant: '',
  disableBackdropClick: false,
  emulation: false
};

export const ModalBase = ModalNotification;

export default withTheme(withStyles(styles)(ModalNotification));
